import React from 'react';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';

/**
 * The private credit facility page's shell on the ABF facility standard (PC workflow parity, slice 2):
 * persistent identity, the header facts a credit desk reads first (with the next best action among
 * them), the facility's workspaces as one navigation, and the work below. Presentation only: Loan 360
 * owns every read and every action and passes them in.
 */

export interface ShellFact {
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'default' | 'emerald' | 'amber' | 'rose';
  onClick?: () => void;
  title?: string;
}

export interface ShellTab { id: string; label: string; count?: number | string; tone?: 'amber' | 'emerald' | 'cyan' | 'red' | 'neutral' }

const Page = styled.section`
  width: min(100%, 100rem);
  margin: 0 auto;
  padding: 1rem 1.25rem 2.5rem;
  box-sizing: border-box;
  color: #e2e8f0;
  min-width: 0;
  button:focus-visible, a:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
  @media (max-width: 760px) { padding: 0.75rem 0.5rem 2rem; }
`;

const ContextHeader = styled.header`
  position: sticky;
  /* Below the 64px app navigation and the 37px context strip. */
  top: 101px;
  z-index: 40;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 16px;
  background: radial-gradient(circle at top right, rgba(16, 122, 90, 0.16), transparent 38%), rgba(5, 12, 24, 0.97);
  box-shadow: 0 18px 55px rgba(2, 6, 23, 0.42);
  backdrop-filter: blur(16px);
  @media (max-width: 1050px), (max-height: 700px) { position: static; }
`;

const HeaderTop = styled.div`
  display: grid;
  grid-template-columns: minmax(19rem, 1.1fr) minmax(32rem, 3fr);
  gap: 1rem;
  padding: 1rem;
  @media (max-width: 1050px) { grid-template-columns: 1fr; }
`;

const Identity = styled.div`
  min-width: 0;
  .back { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0; border: 0; background: transparent; color: #7dd3fc; cursor: pointer; font: inherit; font-size: 0.7rem; font-weight: 800; }
  h1 { margin: 0.55rem 0 0.2rem; color: #f8fafc; font-size: clamp(1.25rem, 2vw, 1.8rem); line-height: 1.15; overflow-wrap: anywhere; }
  .meta { color: #94a3b8; font-size: 0.75rem; line-height: 1.5; }
  .actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.7rem; }
`;

const Facts = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(8.5rem, 1fr));
  gap: 0.55rem;
  @media (max-width: 780px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

const Fact = styled.div<{ $clickable?: boolean }>`
  min-width: 0;
  padding: 0.65rem 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.62);
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: border-color .15s;
  &:hover { border-color: ${p => p.$clickable ? 'rgba(125, 211, 252, 0.6)' : 'rgba(148, 163, 184, 0.14)'}; }
  .label { display: flex; align-items: center; gap: 6px; margin-bottom: 0.28rem; color: #64748b; font-size: 0.58rem; font-weight: 850; letter-spacing: 0.08em; text-transform: uppercase; }
  .value { overflow: hidden; color: #f8fafc; font-size: 0.84rem; font-weight: 850; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .value[data-tone='emerald'] { color: #6ee7b7; }
  .value[data-tone='rose'] { color: #fca5a5; }
  .value[data-tone='amber'] { color: #fcd34d; }
  .sub { overflow: hidden; margin-top: 0.25rem; color: #94a3b8; font-size: 0.62rem; text-overflow: ellipsis; white-space: nowrap; }
`;

const Nav = styled.nav`
  display: flex;
  gap: 0.15rem;
  overflow-x: auto;
  padding: 0 0.75rem;
  border-top: 1px solid rgba(148, 163, 184, 0.13);
  button {
    flex: 0 0 auto; display: inline-flex; align-items: center; gap: 0.4rem;
    padding: 0.75rem 0.68rem; border: 0; border-bottom: 2px solid transparent; background: transparent;
    color: #94a3b8; cursor: pointer; font: inherit; font-size: 0.68rem; font-weight: 800;
  }
  button[aria-current='page'] { border-bottom-color: #38bdf8; color: #e0f2fe; }
  .count { font-size: 0.62rem; padding: 1px 7px; border-radius: 999px; background: rgba(148, 163, 184, 0.14); color: #e2e8f0; }
  .count[data-tone='amber'] { background: rgba(252, 211, 77, 0.16); color: #fcd34d; }
  .count[data-tone='red'] { background: rgba(248, 113, 113, 0.16); color: #fca5a5; }
  .count[data-tone='emerald'] { background: rgba(110, 231, 183, 0.14); color: #6ee7b7; }
`;

const Body = styled.div`
  margin-top: 1.5rem;
  min-width: 0;
  > * + * { margin-top: 1rem; }
`;

interface Props {
  facilityLabel: string;
  back: { label: string; onClick: () => void };
  title: React.ReactNode;
  meta: React.ReactNode;
  actions?: React.ReactNode;
  facts: ShellFact[];
  tabs: ShellTab[];
  activeTab: string;
  onTab: (id: string) => void;
  children: React.ReactNode;
}

const FacilityShell: React.FC<Props> = ({ facilityLabel, back, title, meta, actions, facts, tabs, activeTab, onTab, children }) => (
  <Page aria-label={facilityLabel}>
    <ContextHeader>
      <HeaderTop>
        <Identity>
          <button type="button" className="back" onClick={back.onClick}><ArrowLeft size={14} /> {back.label}</button>
          <h1>{title}</h1>
          <div className="meta">{meta}</div>
          {actions && <div className="actions">{actions}</div>}
        </Identity>
        <Facts aria-label={`${facilityLabel} headline facts`}>
          {facts.map((fact, index) => (
            <Fact key={index} $clickable={Boolean(fact.onClick)} onClick={fact.onClick} title={fact.title}
              role={fact.onClick ? 'button' : undefined} tabIndex={fact.onClick ? 0 : undefined}
              onKeyDown={fact.onClick ? event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); fact.onClick?.(); } } : undefined}>
              <div className="label">{fact.label}</div>
              <div className="value" data-tone={fact.tone || 'default'}>{fact.value}</div>
              {fact.sub !== undefined && <div className="sub">{fact.sub}</div>}
            </Fact>
          ))}
        </Facts>
      </HeaderTop>
      <Nav aria-label={`${facilityLabel} workspaces`}>
        {tabs.map(tab => (
          <button key={tab.id} type="button" aria-current={activeTab === tab.id ? 'page' : undefined} onClick={() => onTab(tab.id)}>
            {tab.label}
            {tab.count !== undefined && <span className="count" data-tone={tab.tone || 'neutral'}>{tab.count}</span>}
          </button>
        ))}
      </Nav>
    </ContextHeader>
    <Body>{children}</Body>
  </Page>
);

export default FacilityShell;
